/**
 * Dynamic Catalog
 * Dynamic filtering, search, download and pagination based on settings
 */

class DynamicCatalog {
  constructor() {
    this.section = document.getElementById('dynamic-catalog-section');
    if (!this.section) return;

    this.filterFields = this.section.dataset.filterFields?.split(',').map(f => f.trim()) || [];
    this.itemsPerPage = parseInt(this.section.dataset.itemsPerPage || '5') || 5;
    this.currentPage = 1;
    
    this.searchInputDesktop = document.getElementById('dynamic-catalog-search-desktop');
    this.searchInputMobile = document.getElementById('dynamic-catalog-search-mobile');
    this.cardsContainer = document.getElementById('dynamic-catalog-cards');
    this.paginationContainer = document.getElementById('dynamic-catalog-pagination');
    /** @type {HTMLElement[]} */
    this.cards = Array.from(this.cardsContainer?.querySelectorAll('.dynamic-catalog-card') || []);

    // UI Elements
    this.downloadBtn = document.getElementById('dynamic-catalog-download-selected');
    this.selectAllBtn = document.getElementById('dynamic-catalog-select-all');
    this.filtersOpenMobileBtn = document.getElementById('dynamic-catalog-filters-open-mobile');
    this.filtersToggleDesktopBtn = document.getElementById('dynamic-catalog-filters-toggle-desktop');
    this.filtersModal = document.getElementById('dynamic-catalog-filters-modal');
    this.filtersModalClose = document.getElementById('dynamic-catalog-filters-modal-close');
    this.filtersModalApply = document.getElementById('dynamic-catalog-filters-modal-apply');
    this.filtersModalBody = document.getElementById('dynamic-catalog-filters-modal-body');
    this.filtersAside = this.section.querySelector('.dynamic-catalog-filters');

    // Blog groups slides
    this.blogGroupSlides = Array.from(this.section?.querySelectorAll('.dynamic-group-slide') || []);
    this.activeBlogGroup = null;

    // Gerar um ID único para esta section baseado na URL
    this.sectionId = this.generateSectionId();
    this.storageKey = `dynamic-catalog-page-${this.sectionId || 'default'}`;

    this.init();
  }

  /**
   * Gerar ID único para a section
   */
  generateSectionId() {
    const url = window.location.pathname;
    const hash = url.split('/').filter(p => p).join('-');
    return hash || 'default';
  }

  /**
   * Obter página guardada no local storage
   */
  getSavedPage() {
    const saved = localStorage.getItem(this.storageKey || '');
    return saved ? parseInt(saved, 10) : 1;
  }

  /**
   * Guardar página no local storage
   * @param {number} page
   */
  savePage(page) {
    localStorage.setItem(this.storageKey || '', String(page));
  }

  init() {

    // Inicializar todos os cards como visíveis no carregamento
    this.cards.forEach(card => {
      card.dataset.filterVisible = '1';
    });

    // Restaurar página guardada
    this.currentPage = this.getSavedPage();

    // Setup listeners
    this.setupSearchListener();
    this.setupCheckboxListeners();
    this.setupCardCheckboxListeners();
    this.setupSelectAllListener();
    this.setupDownloadListener();
    this.setupMobileFiltersListener();
    this.setupDesktopFiltersToggle();
    this.setupSeeMoreLess();
    this.setupBlogGroupSlides();

    // Renderizar paginação inicial
    this.renderPagination();
    this.applyPagination();

    // Initial state
    this.updateDownloadBtn();
    this.setupFilterTitleToggle();

    // If there are more than 250 items, fetch remaining pages in the background
    this.loadAllPages();
  }

  /**
   * Setup Blog Group Slides
   */
  setupBlogGroupSlides() {
    this.blogGroupSlides.forEach(slide => {
      slide.addEventListener('click', () => {
        // Get the title of the clicked slide
        const title = slide.dataset.title;
        
        // Find all slides with the same title (mobile and desktop versions)
        const matchingSlides = this.blogGroupSlides.filter(s => s.dataset.title === title);
        
        // Check if any of them are active
        const isActive = matchingSlides.some(s => s.classList.contains('active'));
        
        // Remove active from all slides
        this.blogGroupSlides.forEach(s => s.classList.remove('active'));
        
        // Add active to all matching slides (unless they were already active)
        if (!isActive) {
          matchingSlides.forEach(s => s.classList.add('active'));
          this.activeBlogGroup = matchingSlides[0]; // Use the first one as reference
        } else {
          this.activeBlogGroup = null;
        }
        
        // Reset page and filter
        this.currentPage = 1;
        this.savePage(1);
        this.filterCards();
      });
    });
  }

  /**
   * Setup pesquisa
   */
  setupSearchListener() {
    // Sincronizar valores entre desktop e mobile
    const syncSearchValues = () => {
      if (this.searchInputDesktop instanceof HTMLInputElement && this.searchInputMobile instanceof HTMLInputElement) {
        if (document.activeElement === this.searchInputDesktop) {
          this.searchInputMobile.value = this.searchInputDesktop.value;
        } else {
          this.searchInputDesktop.value = this.searchInputMobile.value;
        }
      }
    };

    if (this.searchInputDesktop instanceof HTMLInputElement) {
      this.searchInputDesktop.addEventListener('input', () => {
        syncSearchValues();
        this.currentPage = 1;
        this.savePage(1);
        this.filterCards();
      });
    }

    if (this.searchInputMobile instanceof HTMLInputElement) {
      this.searchInputMobile.addEventListener('input', () => {
        syncSearchValues();
        this.currentPage = 1;
        this.savePage(1);
        this.filterCards();
      });
    }
  }

  /**
   * Setup checkbox listeners com event delegation
   */
  setupCheckboxListeners() {
    // Listener para change event em checkboxes da sidebar
    this.filtersAside?.addEventListener('change', (e) => {
      if (e.target instanceof HTMLElement && e.target.classList.contains('dynamic-catalog-filter-checkbox')) {
        this.currentPage = 1;
        this.savePage(1);
        this.filterCards();
      }
    });

    // Listener para input event também (alternativa)
    this.filtersAside?.addEventListener('input', (e) => {
      if (e.target instanceof HTMLElement && e.target.classList.contains('dynamic-catalog-filter-checkbox')) {
        this.currentPage = 1;
        this.savePage(1);
        this.filterCards();
      }
    });

    // Listener para checkboxes no modal
    this.filtersModal?.addEventListener('change', (e) => {
      if (e.target instanceof HTMLElement && e.target.classList.contains('dynamic-catalog-filter-checkbox')) {
      }
    });
  }

  /**
   * Setup card checkbox listeners
   */
  setupCardCheckboxListeners() {
    // Usar event delegation para ouvir checkboxes dos cards
    this.cardsContainer?.addEventListener('change', (e) => {
      if (e.target instanceof HTMLElement && e.target.classList.contains('dynamic-catalog-checkbox')) {
        this.updateDownloadBtn();
      }
    });

    // Listener para input event também (alternativa)
    this.cardsContainer?.addEventListener('input', (e) => {
      if (e.target instanceof HTMLElement && e.target.classList.contains('dynamic-catalog-checkbox')) {
        this.updateDownloadBtn();
      }
    });
  }

  /**
   * Setup Select All
   */
  setupSelectAllListener() {
    if (!this.selectAllBtn) return;
    this.selectAllBtn.addEventListener('click', () => {
      const visibleCards = this.getVisibleCards();
      const allSelected = visibleCards.every(card => {
        const checkbox = this.getCheckbox(card);
        return checkbox instanceof HTMLInputElement && checkbox.checked;
      });

      visibleCards.forEach(card => {
        const checkbox = this.getCheckbox(card);
        if (checkbox instanceof HTMLInputElement) {
          checkbox.checked = !allSelected;
        }
      });

      this.updateDownloadBtn();
    });
  }

  /**
   * Setup Download
   */
  setupDownloadListener() {
    if (!this.downloadBtn) return;
    this.downloadBtn.addEventListener('click', () => {
      const checkedCards = this.cards.filter(card => {
        const checkbox = this.getCheckbox(card);
        return checkbox instanceof HTMLInputElement && checkbox.checked;
      });

      checkedCards.forEach(card => {
        const links = card.querySelectorAll('.dynamic-catalog-download-link');
        links.forEach(link => {
          if (link instanceof HTMLAnchorElement) {
            const a = document.createElement('a');
            a.href = link.href;
            a.download = link.getAttribute('download') || '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
        });
      });
    });
  }

  /**
   * Setup Desktop Filters Toggle
   */
  setupDesktopFiltersToggle() {
    if (!this.filtersToggleDesktopBtn || !this.filtersAside) return;

    this.filtersToggleDesktopBtn.addEventListener('click', () => {
      if (this.filtersAside instanceof HTMLElement) {
        const isHidden = this.filtersAside.dataset.hidden === '1';
        
        if (isHidden) {
          // Mostrar filtros
          this.filtersAside.dataset.hidden = '0';
          this.filtersAside.style.display = 'block';
        } else {
          // Esconder filtros
          this.filtersAside.dataset.hidden = '1';
          this.filtersAside.style.display = 'none';
        }
      }
    });
  }

  /**
   * Setup Mobile Filters Modal
   */
  setupMobileFiltersListener() {
    if (!this.filtersOpenMobileBtn || !this.filtersModal) return;

    // Abrir modal
    this.filtersOpenMobileBtn.addEventListener('click', () => {
      if (this.filtersAside && this.filtersModalBody) {
        this.filtersModalBody.innerHTML = this.filtersAside.innerHTML;
        
        // Sincronizar estado atual do sidebar para o modal
        const sidebarCheckboxes = this.filtersAside.querySelectorAll('.dynamic-catalog-filter-checkbox:checked');
        sidebarCheckboxes.forEach(sidebarCb => {
          if (sidebarCb instanceof HTMLInputElement) {
            const field = sidebarCb.getAttribute('data-filter');
            const value = sidebarCb.value;
            const modalCb = this.filtersModalBody?.querySelector(
              `.dynamic-catalog-filter-checkbox[data-filter="${field}"][value="${value}"]`
            );
            if (modalCb instanceof HTMLInputElement) {
              modalCb.checked = true;
            }
          }
        });

        this.setupSeeMoreLess(this.filtersModalBody);
        if (this.filtersModal instanceof HTMLElement) {
          this.filtersModal.style.display = 'flex';
        }
      }
    });

    // Fechar modal
    if (this.filtersModalClose) {
      this.filtersModalClose.addEventListener('click', () => {
        if (this.filtersModal instanceof HTMLElement) {
          this.filtersModal.style.display = 'none';
        }
      });
    }

    // Backdrop click to close
    const backdrop = this.filtersModal?.querySelector('.dynamic-catalog-filters-modal-backdrop');
    if (backdrop instanceof HTMLElement) {
      backdrop.addEventListener('click', () => {
        if (this.filtersModal instanceof HTMLElement) {
          this.filtersModal.style.display = 'none';
        }
      });
    }

    // Aplicar filtros
    if (this.filtersModalApply) {
      this.filtersModalApply.addEventListener('click', () => {
        // Sincronizar checkboxes do modal para o sidebar por field e value
        if (this.filtersModalBody && this.filtersAside) {
          const modalCheckboxes = this.filtersModalBody.querySelectorAll('.dynamic-catalog-filter-checkbox');
          const sidebarCheckboxes = this.filtersAside.querySelectorAll('.dynamic-catalog-filter-checkbox');
          
          // Primeiro, desmarcar TODOS os checkboxes do sidebar
          sidebarCheckboxes.forEach(cb => {
            if (cb instanceof HTMLInputElement) {
              cb.checked = false;
            }
          });
          
          // Depois, marcar apenas os que estão marcados no modal
          modalCheckboxes.forEach((modalCb) => {
            if (modalCb instanceof HTMLInputElement && modalCb.checked) {
              const field = modalCb.getAttribute('data-filter');
              const value = modalCb.value;
              const sidebarCb = this.filtersAside?.querySelector(
                `.dynamic-catalog-filter-checkbox[data-filter="${field}"][value="${value}"]`
              );
              if (sidebarCb instanceof HTMLInputElement) {
                sidebarCb.checked = true;
              }
            }
          });
        }

        // Chamar filterCards uma vez após sincronizar todos
        this.filterCards();
        if (this.filtersModal instanceof HTMLElement) {
          this.filtersModal.style.display = 'none';
        }
      });
    }
  }

  /**
   * Setup See More/Less buttons
   * @param {HTMLElement | null} [scope=null]
   */
  setupSeeMoreLess(scope = this.section) {
    if (!scope || !(scope instanceof HTMLElement)) return;

    scope.querySelectorAll('.dynamic-catalog-filter-see-more').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLElement)) return;
        const filter = btn.getAttribute('data-filter');
        const filterGroup = scope.querySelector(`[data-filter="${filter}"]`);
        if (filterGroup instanceof HTMLElement) {
          filterGroup.querySelectorAll('.dynamic-catalog-filter-item').forEach((li, idx) => {
            if (idx >= 3) li.classList.remove('dynamic-catalog-filter-item-hidden');
          });
          btn.style.display = 'none';
          const lessBtn = filterGroup.querySelector('.dynamic-catalog-filter-see-less');
          if (lessBtn instanceof HTMLElement) lessBtn.style.display = 'inline-block';
        }
      });
    });

    scope.querySelectorAll('.dynamic-catalog-filter-see-less').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLElement)) return;
        const filter = btn.getAttribute('data-filter');
        const filterGroup = scope.querySelector(`[data-filter="${filter}"]`);
        if (filterGroup instanceof HTMLElement) {
          filterGroup.querySelectorAll('.dynamic-catalog-filter-item').forEach((li, idx) => {
            if (idx >= 3) li.classList.add('dynamic-catalog-filter-item-hidden');
          });
          btn.style.display = 'none';
          const moreBtn = filterGroup.querySelector('.dynamic-catalog-filter-see-more');
          if (moreBtn instanceof HTMLElement) moreBtn.style.display = 'inline-block';
        }
      });
    });
  }

  /**
   * Filtragem de cards
   */
  filterCards() {
    let searchValue = '';
    if (this.searchInputDesktop instanceof HTMLInputElement) {
      searchValue = this.searchInputDesktop.value;
    } else if (this.searchInputMobile instanceof HTMLInputElement) {
      searchValue = this.searchInputMobile.value;
    }
    const search = searchValue.trim().toLowerCase();

    // Obter valores selecionados para cada filtro (apenas do sidebar, não do modal)
    /** @type {{[key: string]: string[]}} */
    const selectedFilters = {};
    if (!this.filterFields || !this.filtersAside) return;
    
    this.filterFields.forEach(field => {
      const checkboxes = Array.from(
        this.filtersAside?.querySelectorAll(`.dynamic-catalog-filter-checkbox[data-filter="${field}"]:checked`) || []
      );
      selectedFilters[field] = checkboxes.map(cb => {
        if (cb instanceof HTMLInputElement) {
          return cb.value;
        }
        return '';
      }).filter(v => v.length > 0);
    });

    // Obter lista de posts do blog group ativo
    let allowedPosts = null;
    if (this.activeBlogGroup) {
      const postsData = this.activeBlogGroup.dataset.posts;
      if (postsData) {
        allowedPosts = postsData.split(',').map(p => p.trim());
      }
    }

    this.cards.forEach(card => {
      let visible = true;

      if (visible && allowedPosts) {
        const cardTitle = card.dataset.title || '';
        if (!allowedPosts.includes(cardTitle)) {
          visible = false;
        }
      }

      if (visible && search && !card.dataset.title?.toLowerCase().includes(search)) {
        visible = false;
      }

      if (visible && this.filterFields) {
        try {
          const itemJsonStr = card.dataset.item || '';
          const itemData = JSON.parse(itemJsonStr);
          
          this.filterFields.forEach(field => {
            const selectedValues = selectedFilters[field];
            if (selectedValues && selectedValues.length > 0) {
              const fieldValue = itemData[field];
              /** @type {string[]} */
              let cardValues = [];

              if (Array.isArray(fieldValue)) {
                cardValues = fieldValue.map(v => (typeof v === 'string' ? v.trim() : String(v).trim()));
              } else if (fieldValue) {
                cardValues = String(fieldValue)
                  .split(',')
                  .map(v => v.trim())
                  .filter(v => v.length > 0);
              }

              if (!cardValues.some(v => selectedValues.includes(v))) {
                visible = false;
              }
            }
          });
        } catch (e) {
          console.error('Erro ao parsear dados do card:', e);
          visible = false;
        }
      }

      card.dataset.filterVisible = visible ? '1' : '0';
    });

    this.renderPagination();
    this.applyPagination();
    this.updateDownloadBtn();
  }

  getPaginationRange() {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    return isMobile ? { edge: 1, wing: 1 } : { edge: 3, wing: 2 };
  }

  renderPagination() {
    if (!this.paginationContainer) return;

    const visibleCards = this.getVisibleCards();
    const totalPages = this.itemsPerPage ? Math.ceil(visibleCards.length / this.itemsPerPage) : 1;

    this.paginationContainer.innerHTML = '';

    if (totalPages <= 1) {
      return;
    }

    if (this.currentPage && this.currentPage > totalPages) {
      this.currentPage = totalPages;
      this.savePage(this.currentPage);
    }

    const buttonsWrapper = document.createElement('div');
    buttonsWrapper.className = 'dynamic-catalog-pagination-buttons';

    const prevBtn = document.createElement('button');
    prevBtn.className = 'dynamic-catalog-pagination-btn';
    prevBtn.textContent = '←';
    prevBtn.disabled = !this.currentPage || this.currentPage === 1;
    prevBtn.addEventListener('click', () => {
      if (this.currentPage && this.currentPage > 1) {
        this.currentPage--;
        this.savePage(this.currentPage);
        this.renderPagination();
        this.applyPagination();
        this.scrollToTop();
      }
    });
    buttonsWrapper.appendChild(prevBtn);

    /**
     * Build the list of page slots to render.
     * Always shows the first EDGE pages and last EDGE pages.
     * Around the current page, shows WING pages on each side.
     * Gaps are represented as null (rendered as '…').
     */
    const { edge: EDGE, wing: WING } = this.getPaginationRange();

    /** @type {(number | null)[]} */
    const slots = [];

    // Collect pages in order
    const pages = new Set();
    for (let i = 1; i <= Math.min(EDGE, totalPages); i++) pages.add(i);
    for (let i = totalPages - EDGE + 1; i <= totalPages; i++) if (i >= 1) pages.add(i);
    const cur = this.currentPage || 1;
    for (let i = cur - WING; i <= cur + WING; i++) if (i >= 1 && i <= totalPages) pages.add(i);

    const sorted = [...pages].sort((a, b) => a - b);

    let prev = 0;
    for (const p of sorted) {
      if (prev && p - prev > 1) slots.push(null); // gap → ellipsis
      slots.push(p);
      prev = p;
    }

    for (const slot of slots) {
      if (slot === null) {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'dynamic-catalog-pagination-ellipsis';
        ellipsis.textContent = '…';
        buttonsWrapper.appendChild(ellipsis);
      } else {
        const pageBtn = document.createElement('button');
        pageBtn.className = 'dynamic-catalog-pagination-btn';
        if (slot === this.currentPage) pageBtn.classList.add('active');
        pageBtn.textContent = String(slot);
        pageBtn.addEventListener('click', () => {
          this.currentPage = slot;
          this.savePage(this.currentPage);
          this.renderPagination();
          this.applyPagination();
          this.scrollToTop();
        });
        buttonsWrapper.appendChild(pageBtn);
      }
    }

    const nextBtn = document.createElement('button');
    nextBtn.className = 'dynamic-catalog-pagination-btn';
    nextBtn.textContent = '→';
    nextBtn.disabled = !this.currentPage || this.currentPage === totalPages;
    nextBtn.addEventListener('click', () => {
      if (this.currentPage && this.currentPage < totalPages) {
        this.currentPage++;
        this.savePage(this.currentPage);
        this.renderPagination();
        this.applyPagination();
        this.scrollToTop();
      }
    });
    buttonsWrapper.appendChild(nextBtn);

    this.paginationContainer.appendChild(buttonsWrapper);

    const info = document.createElement('div');
    info.className = 'dynamic-catalog-pagination-info';
    info.textContent = `Página ${this.currentPage} de ${totalPages}`;
    this.paginationContainer.appendChild(info);
  }

  applyPagination() {
    if (!this.currentPage || !this.itemsPerPage) return;
    
    const visibleCards = this.getVisibleCards();
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;

    this.cards.forEach(card => {
      if (card instanceof HTMLElement) {
        card.style.display = 'none';
      }
    });

    visibleCards.forEach((card, index) => {
      if (index >= start && index < end && card instanceof HTMLElement) {
        card.style.display = '';
      }
    });
  }

  scrollToTop() {
    const cardsTop = this.cardsContainer?.getBoundingClientRect().top;
    if (cardsTop) {
      window.scrollBy({
        top: cardsTop - 100,
        behavior: 'smooth'
      });
    }
  }

  updateDownloadBtn() {
    const anyChecked = this.cards.some(card => {
      const checkbox = this.getCheckbox(card);
      if (checkbox instanceof HTMLInputElement) {
        return checkbox.checked;
      }
      return false;
    });
    if (this.downloadBtn instanceof HTMLButtonElement) {
      this.downloadBtn.disabled = !anyChecked;
    }
  }

  /**
   * Setup Filter Title Toggle
   */
  setupFilterTitleToggle() {
    if (!this.filtersAside) return;

    this.filtersAside.addEventListener('click', (e) => {
      const target = e.target;
      if (target instanceof HTMLElement && target.classList.contains('dynamic-catalog-filter-title')) {
        const filterGroup = target.closest('.dynamic-catalog-filter-group');
        if (filterGroup instanceof HTMLElement) {
          filterGroup.classList.toggle('collapsed');
        }
      }
    });
  }

  /**
   * Helpers
   */
  /**
   * Progressively load all paginated pages so client-side filtering
   * sees every item. Shopify's {% paginate %} cap is 250, so collections
   * larger than 250 require multiple full-page fetches.
   *
   * NOTE: We intentionally do NOT use the Section Rendering API (?section_id=)
   * here because Shopify does not honour the `page` URL parameter when
   * rendering a section in isolation on a non-list template (pages/, etc.).
   * Fetching the full page HTML with ?page=N guarantees the {% paginate %}
   * tag sees the correct page context and returns the right slice of items.
   */
  async loadAllPages() {
    // Use Shopify's own paginate.next.url so we never construct wrong page URLs.
    // Each fetched page exposes the next URL via data-next-url, forming a chain.
    let nextUrl = this.cardsContainer?.dataset.nextUrl || '';
    if (!nextUrl) return;

    // Show a subtle loading indicator above the pagination controls
    const loadingEl = document.createElement('div');
    loadingEl.className = 'dynamic-catalog-loading';
    loadingEl.style.cssText = 'text-align:center;padding:8px;font-size:.875rem;opacity:.6;';
    loadingEl.textContent = 'A carregar todos os itens\u2026';
    this.paginationContainer?.before(loadingEl);

    while (nextUrl) {
      try {
        const response = await fetch(nextUrl);
        if (!response.ok) break;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextContainer = doc.querySelector('#dynamic-catalog-cards');
        const newCards = nextContainer?.querySelectorAll('.dynamic-catalog-card') || [];

        newCards.forEach(card => {
          if (card instanceof HTMLElement) {
            card.dataset.filterVisible = '1';
            this.cardsContainer?.appendChild(card);
            this.cards.push(card);
          }
        });

        // Advance to the next page using Shopify's generated URL from this page
        nextUrl = nextContainer?.getAttribute('data-next-url') || '';

        if (newCards.length > 0) {
          this.filterCards();
        }
      } catch (e) {
        console.warn('DynamicCatalog: failed to load page', nextUrl, e);
        break;
      }
    }

    loadingEl.remove();

    // Add any filter values discovered in lazy-loaded pages (items 251+)
    this.updateFilterOptions();
  }

  /**
   * Scan all loaded cards and add to the sidebar any filter checkbox options
   * that were not present in the first 250 items rendered server-side.
   */
  updateFilterOptions() {
    if (!this.filtersAside || !this.filterFields) return;

    this.filterFields.forEach(field => {
      const existingValues = new Set(
        Array.from(
          this.filtersAside?.querySelectorAll(`.dynamic-catalog-filter-checkbox[data-filter="${field}"]`) || []
        ).map(cb => (cb instanceof HTMLInputElement ? cb.value : '')).filter(v => v)
      );

      /** @type {Set<string>} */
      const newValues = new Set();

      this.cards.forEach(card => {
        try {
          const itemData = JSON.parse(card.dataset.item || '{}');
          const fieldValue = itemData[field];
          /** @type {string[]} */
          let cardValues = [];
          if (Array.isArray(fieldValue)) {
            cardValues = fieldValue.map(v => String(v).trim()).filter(v => v);
          } else if (fieldValue) {
            cardValues = String(fieldValue).split(',').map(v => v.trim()).filter(v => v);
          }
          cardValues.forEach(v => { if (!existingValues.has(v)) newValues.add(v); });
        } catch (e) { /* skip malformed card data */ }
      });

      if (newValues.size === 0) return;

      const filterGroup = this.filtersAside?.querySelector(`.dynamic-catalog-filter-group[data-filter="${field}"]`);
      const list = filterGroup?.querySelector('.dynamic-catalog-filter-list');
      if (!list) return;

      // Re-check see-more/see-less visibility after new items are added
      const currentCount = list.querySelectorAll('.dynamic-catalog-filter-item').length;
      let idx = currentCount;

      newValues.forEach(value => {
        idx++;
        const li = document.createElement('li');
        li.className = idx > 3 ? 'dynamic-catalog-filter-item dynamic-catalog-filter-item-hidden' : 'dynamic-catalog-filter-item visible';
        const safeValue = value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        li.innerHTML = `
          <label class="dynamic-catalog-filter-label">
            <input type="checkbox" class="dynamic-catalog-filter-checkbox" value="${safeValue}" data-filter="${field}">
            <span class="dynamic-catalog-filter-value">${safeValue}</span>
          </label>`;
        list.appendChild(li);
      });

      // Show the see-more button if total now exceeds 3
      if (currentCount <= 3 && idx > 3) {
        const moreBtn = filterGroup?.querySelector('.dynamic-catalog-filter-see-more');
        if (moreBtn instanceof HTMLElement) moreBtn.style.display = 'inline-block';
      }
    });
  }

  /**
   * Get checkbox from card
   * @param {HTMLElement} card
   * @returns {HTMLInputElement | null}
   */
  getCheckbox(card) {
    const checkbox = card.querySelector('.dynamic-catalog-checkbox');
    if (checkbox instanceof HTMLInputElement) {
      return checkbox;
    }
    return null;
  }

  getVisibleCards() {
    return this.cards.filter(card => card.dataset.filterVisible === '1');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new DynamicCatalog();
});
